import { AUTH_FRAGMENT } from '@webgame/shared';
import { render } from 'preact';
import { SessionClient } from './core/session/session-client';
import { createTabTokenStore } from './core/session/token-store';
import { createClientTranslator } from './i18n/client-translator';
import { App } from './ui/app';
import { configureGame, startGame } from './ui/screens/game-screen-loader';
import { BootProvider, type BootServices } from './ui/services';
// The HUD's faces (ADR 0047): bundled, fetched only once something uses them.
import '@fontsource/cinzel/latin-700.css';
import '@fontsource/literata/latin-400.css';
import '@fontsource/literata/latin-600.css';
import '@fontsource/literata/latin-700.css';
import '@fontsource/oswald/latin-500.css';
import '@fontsource/oswald/latin-700.css';
import '@fontsource/spectral/latin-600.css';
import './ui/styles/tokens.css';
import './ui/styles/base.css';
import './ui/styles/primitives.css';
import './ui/styles/components.css';

/**
 * Reloads the page for a newer server build (ADR 0049), at most once a minute: a page that
 * reloaded and still disagrees ends its session instead of reloading in a loop.
 */
function reloadForNewBuild(): boolean {
  const key = 'webgame.reloadedForBuild';
  const now = Date.now();
  try {
    const last = Number(sessionStorage.getItem(key) ?? 0);
    if (now - last < 60_000) return false;
    sessionStorage.setItem(key, String(now));
  } catch {
    // Without storage there is no loop guard: reload anyway, a stale page is worse.
  }
  globalThis.location.reload();
  return true;
}

async function bootstrap(): Promise<void> {
  const root = document.getElementById('app');
  if (!root) throw new Error('Missing #app root element');

  // Only the player's language is downloaded, before the first screen (NET-CLIENT-CACHE C2).
  const translator = await createClientTranslator();
  // Only the session before the first screen (NET-CLIENT-CACHE C3): the game, its rules and its
  // simulation come in their own chunk, fetched and made while the player chooses.
  const session: SessionClient = new SessionClient({
    tokens: createTabTokenStore(),
    enter: async (player) => {
      const game = await startGame();
      await game.services.client.enter(player);
    },
  });
  const services: BootServices = { session, translator };
  configureGame(services, { reloadPage: reloadForNewBuild, combatTrace: import.meta.env.DEV });
  const updateTitle = (): void => {
    document.title = services.translator.t('common.appName');
  };
  updateTitle();
  services.translator.subscribe(updateTitle);

  render(
    <BootProvider services={services}>
      <App />
    </BootProvider>,
    root,
  );

  const fragment = takeSignInFragment();
  if (fragment?.handoff) {
    void session.completeBrowserSignIn(fragment.handoff);
  } else {
    if (fragment?.error) session.failSignIn(fragment.error);
    // Resume this tab's session after a reload; falls back to the sign-in screen.
    void session.restore().catch(() => undefined);
  }
}

/**
 * What a browser sign-in brought back in the URL fragment (ADR 0029), removed from the address
 * bar and history at once so the single-use code is never bookmarked or shared.
 */
function takeSignInFragment(): { handoff?: string; error?: string } | null {
  const params = new URLSearchParams(globalThis.location.hash.slice(1));
  const handoff = params.get(AUTH_FRAGMENT.handoff);
  const error = params.get(AUTH_FRAGMENT.error);
  if (handoff === null && error === null) return null;
  globalThis.history.replaceState(null, '', globalThis.location.pathname);
  return { ...(handoff && { handoff }), ...(error && { error }) };
}

void bootstrap();
